import {
    createContext,
    useContext,
    useState,
    useSyncExternalStore,
} from "react";

/**
 * Which cards on a board have their drawer expanded, by item id. `Board` keeps
 * one in memory, so a card's drawer stays as it was when windowing remounts it
 * or a live move re-renders it elsewhere, and resets on reload.
 */
export interface BoardCardExpansionStore {
    isExpanded: (itemId: string) => boolean;
    toggle: (itemId: string) => void;
    subscribe: (listener: () => void) => () => void;
}

export const createBoardCardExpansionStore = (): BoardCardExpansionStore => {
    const expandedItemIds = new Set<string>();
    const listeners = new Set<() => void>();
    return {
        isExpanded: (itemId) => expandedItemIds.has(itemId),
        toggle: (itemId) => {
            if (!expandedItemIds.delete(itemId)) {
                expandedItemIds.add(itemId);
            }
            listeners.forEach((listener) => listener());
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
};

export const BoardCardExpansionContext =
    createContext<BoardCardExpansionStore | null>(null);

/** The item a board card shows, provided wherever the board renders one. */
export const BoardCardItemIdContext = createContext<string | null>(null);

const subscribeToNothing = () => () => undefined;

/**
 * Whether the card's drawer is expanded, and a toggle. On a board the board
 * keeps it per item; anywhere else the card keeps it while mounted.
 */
export const useBoardCardExpansion = () => {
    const store = useContext(BoardCardExpansionContext);
    const itemId = useContext(BoardCardItemIdContext);
    const [isLocallyExpanded, setIsLocallyExpanded] = useState(false);
    const boardItem =
        store !== null && itemId !== null ? { store, itemId } : null;
    const isStoredExpanded = useSyncExternalStore(
        boardItem?.store.subscribe ?? subscribeToNothing,
        () => boardItem?.store.isExpanded(boardItem.itemId) ?? false,
    );

    return boardItem === null
        ? {
              isExpanded: isLocallyExpanded,
              toggle: () => setIsLocallyExpanded((isExpanded) => !isExpanded),
          }
        : {
              isExpanded: isStoredExpanded,
              toggle: () => boardItem.store.toggle(boardItem.itemId),
          };
};
